import { formatDate } from '@angular/common';
import * as i0 from '@angular/core';
import { LOCALE_ID, Injectable, Inject } from '@angular/core';
const IsSameDayHelper = (firstDate, secondDate) => {
  if (!firstDate || !secondDate) {
    return false;
  }
  if (typeof firstDate === 'string') {
    firstDate = DateOnlyStringToDateHelper(firstDate);
  }
  if (typeof secondDate === 'string') {
    secondDate = DateOnlyStringToDateHelper(secondDate);
  }
  return firstDate.getDate() === secondDate.getDate() && firstDate.getMonth() === secondDate.getMonth() && firstDate.getFullYear() === secondDate.getFullYear();
};
const DateOnlyStringToDateHelper = dateStr => {
  return new Date(`${dateStr}T00:00:00`);
};
const DateToStringDateOnlyHyphenHelper = date => {
  return formatDate(date, 'yyyy-MM-dd', 'en-US');
};
let DateFormatService = /*#__PURE__*/(() => {
  class DateFormatService {
    constructor(localeId) {
      this.localeId = localeId;
    }
    /**
     * Converts js date object to js locale.
     * @param date takes a js date object.
     * @returns date string formatted to browser locale e.g. '2022-01-24'.
     */
    dateToStringDateOnlyHyphen(date) {
      if (typeof date === 'string') {
        date = new Date(date);
      }
      return formatDate(date, 'yyyy-MM-dd', this.localeId);
    }
    /**
     * Converts a date only string to a js date object.
     * @param dateStr date only string with hyphens - '2022-01-24'.
     * @returns date object at 00:00:00 in the local timezone of the system.
     */
    dateOnlyStringToDate(dateStr) {
      return new Date(`${dateStr}T00:00:00`);
    }
    /**
     * Removes the time element from a js date object.
     * @param date date object.
     * @returns date object at 00:00:00 in the local timezone of the system.
     */
    getDateWithoutTime(date) {
      if (!date) {
        date = new Date();
      }
      return new Date(date.setHours(0, 0, 0, 0));
    }
    /** Takes a string (or date) and converts to new Date. */
    dateStringToDate(dateStr) {
      if (!dateStr) {
        return new Date();
      }
      return new Date(dateStr);
    }
    /**
     * Checks if the supplied date is the same.
     * @param firstDate js date object.
     * @param secondDate js date object to compare with.
     * @returns true if the supplied date is current date.
     */
    isSameDay(firstDate, secondDate) {
      if (!firstDate || !secondDate) {
        return false;
      }
      if (typeof firstDate === 'string') {
        firstDate = this.dateOnlyStringToDate(firstDate);
      }
      if (typeof secondDate === 'string') {
        secondDate = this.dateOnlyStringToDate(secondDate);
      }
      return firstDate.getDate() === secondDate.getDate() && firstDate.getMonth() === secondDate.getMonth() && firstDate.getFullYear() === secondDate.getFullYear();
    }
    /** Check if the two dates are in the same month. */
    isSameMonth(firstDate, secondDate) {
      if (!firstDate || !secondDate) {
        return false;
      }
      if (typeof firstDate === 'string') {
        firstDate = this.dateOnlyStringToDate(firstDate);
      }
      if (typeof secondDate === 'string') {
        secondDate = this.dateOnlyStringToDate(secondDate);
      }
      return firstDate.getMonth() === secondDate.getMonth() && firstDate.getFullYear() === secondDate.getFullYear();
    }
    /** Check if the second date (as midnight) occurs before the first. */
    isDayBeforeFirstDate(firstDate, secondDate) {
      if (!firstDate || !secondDate) {
        return false;
      }
      if (typeof firstDate === 'string') {
        firstDate = this.dateOnlyStringToDate(firstDate);
      }
      if (typeof secondDate === 'string') {
        secondDate = this.dateOnlyStringToDate(secondDate);
      }
      firstDate = new Date(firstDate.setHours(0, 0, 0, 0));
      secondDate = new Date(secondDate.setHours(0, 0, 0, 0));
      return secondDate.getTime() - firstDate.getTime() < 0;
    }
    /** Process date or dateStr to return Mon, 1 Nov 2021. */
    dayDateString(date) {
      if (!date) {
        return '';
      }
      if (typeof date === 'string') {
        date = this.dateOnlyStringToDate(date);
      }
      return formatDate(date, 'EEE, dd MMM yyyy', this.localeId);
    }
    /** Process date or dateStr to return 21 Nov 21. */
    shortDateString(date) {
      if (!date) {
        return '';
      }
      if (typeof date === 'string') {
        date = this.dateOnlyStringToDate(date);
      }
      return formatDate(date, 'dd MMM yy', this.localeId);
    }
    /**
     * Add any number of days to a base date.
     * @returns day Date String.
     */
    addDays(date, days) {
      const result = new Date(date);
      result.setDate(date.getDate() + (days || 0));
      return this.dayDateString(result);
    }
    /**
     * Add any number of years to a base date.
     * @returns date object.
     */
    addYears(date, years) {
      const result = new Date(date);
      result.setFullYear(date.getFullYear() + (years || 0));
      return result;
    }
    /**
     * Converts standard date object to dateIndex to pass to API.
     * @param date date object or string or undefined.
     * @param isResultNumber default returns as a string.
     * @returns number in format 20210104 for yyyyMMdd as number or string.
     */
    toDateIndex(date, isResultNumber = false) {
      if (!date) {
        return undefined;
      }
      const result = formatDate(date, 'yyyyMMdd', this.localeId);
      return isResultNumber ? parseInt(result, 10) : result;
    }
    /**
     * Converts a 8 digit dateIndex as to a standard date object
     * @param date string or number or undefined - 20220820
     * @returns Date object or undefined if invalid date passed
     */
    dateIndexToDate(date) {
      date = typeof date === 'number' ? date.toString() : date;
      if (typeof date !== 'string' || date.length !== 8 || !parseInt(date)) {
        return undefined;
      }
      const year = parseInt(date.substring(0, 4), 10);
      const month = parseInt(date.substring(4, 6), 10) - 1; // corrects index
      const day = parseInt(date.substring(6, 8), 10);
      return new Date(year, month, day);
    }
    /** Helper to remove format '09:00' as 900. */
    hourStringToNumber(hour) {
      const hourResult = parseInt(hour.replace(':', ''), 10);
      return Number.isNaN(hourResult) ? 0 : hourResult;
    }
    /**
     * A session has a date string and a time string separately.
     * @param date string format date such as '24-02-2022'
     * @param startTime string format 24hr time such as '14:00'
     * @returns Date object
     */
    sessionDateToDate(date, startTime) {
      return new Date(`${date}T${startTime}:00`);
    }
    /** Creates a list of hour number with related display time starting at 700/'7:00 AM' */
    populateTimeToSendList(startTime, length) {
      const timeValues = Array.from(Array(length), (_, x) => x * 100 + startTime);
      const timeList = [];
      timeValues.forEach(value => {
        const isAM = value < 1200;
        let labelValue = value.toString();
        if (!isAM) {
          const pmValue = value - 1200;
          labelValue = (pmValue || 1200).toString();
        }
        timeList.push({
          value: value,
          label: `${labelValue.slice(0, -2)}:${labelValue.slice(-2)} ${isAM ? 'AM' : 'PM'}`
        });
      });
      return timeList;
    }
    /**
     * Checks a date plus a drift against current time for 'freshness'.
     * @param olderDate date to test if fresh as js Date object.
     * @param maxAge the time difference to now that is allowed in milliseconds - e.g. 60000 for 10min.
     * @returns is fresh if the date + maxAge is less that current time in milliseconds.
     */
    isDateFresh(olderDate, maxAge) {
      if (typeof olderDate === 'string') {
        olderDate = new Date(olderDate);
      }
      const now = new Date().getTime();
      const delta = olderDate.getTime() + maxAge - now;
      return delta > 0;
    }
    /**
     * Venue timezone in VenueSettings is represented as a string number as '10' for UTC+10
     * new Date() gets the browsers local time, and .getTimeZoneOffset() returns the -offset in minutes.
     * Therefore we can calculate the browsers current time in relation to the venue
     * for example the browser is in UTC+10 (at 9:52am) and the venue is in UTC+8 (at 7:52am).
     * @returns related venueTime in ms.
     */
    venueTimeMs(timeZone) {
      const venueTimeZoneOffsetMin = timeZone * -60;
      const today = new Date();
      const browserTimeZoneOffset = today.getTimezoneOffset();
      const msDifference = (venueTimeZoneOffsetMin - browserTimeZoneOffset) * 60 * 1000;
      return today.getTime() - msDifference;
    }
    /** Gets the current venue time as a date. */
    currentVenueTime(timeZone) {
      return new Date(this.venueTimeMs(timeZone));
    }
    /**
     * Calculate time with am/pm from date.
     * Expect 8:00pm - notice lack of space and lowercase.
     */
    calcMeridiem(date) {
      if (typeof date === 'string') {
        date = new Date(date);
      }
      const options = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      };
      return date.toLocaleString('en-US', options).toLocaleLowerCase().replace(/\s/g, '');
    }
  }
  DateFormatService.ɵfac = function DateFormatService_Factory(t) {
    return new (t || DateFormatService)(i0.ɵɵinject(LOCALE_ID));
  };
  DateFormatService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DateFormatService,
    factory: DateFormatService.ɵfac,
    providedIn: 'root'
  });
  return DateFormatService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Formats a date/string using the Angular formatDate function.
 * @param date - The date or date string to format
 * @param format - The Angular format to use
 * @param locale - The locale to use when formatting the date via Angular
 */
function rdlFormatDate(date, format, locale) {
  return formatDate(rdlConvertToDate(date), format, locale);
}
/**
 * Converts a string to a Date object - ensures params are a date
 * @param date
 */
function rdlConvertToDate(date) {
  if (!(date instanceof Date)) {
    return new Date(date);
  }
  return date;
}
/**
 * Convert a timeIndex (such as session startTime) to a date set with that time.
 * @param timeIndex 800 (8am), 1300 (1pm)
 * @param startDate Optional start date to use for the time e.g. pass the Venue DateTime vs default local DateTime
 */
function rdlConvertTimeIndexToDate(timeIndex, startDate) {
  timeIndex = typeof timeIndex === 'string' ? parseInt(timeIndex, 10) : timeIndex;
  const hours = Math.floor(timeIndex / 100); // Extract hours
  const minutes = timeIndex % 100; // Extract minutes
  const date = startDate || new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  return date;
}
/**
 * Convert a date to a timeIndex (such as session startTime).
 * @param date A date object
 * @returns timeIndex (e.g., 800 for 8am, 1300 for 1pm)
 */
function rdlConvertDateToTimeIndex(date) {
  date = rdlConvertToDate(date);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return hours * 100 + minutes;
}
/**
 * Converts a timeIndex to a 4 digit string. Used in session time format preparation.
 * @param sessionTime The session time in 24-hour format (e.g., 1700) as a number, string, or date
 */
function rdlConvertTimeIndexToString(sessionTime) {
  if (sessionTime instanceof Date) {
    sessionTime = rdlConvertDateToTimeIndex(sessionTime).toString();
  }
  if (typeof sessionTime === 'number') {
    sessionTime = sessionTime.toString();
  }
  sessionTime = sessionTime.padStart(4, '0');
  return sessionTime;
}
/**
 * Venue timezone offset in is represented as a number as 1000 for UTC+10, -1000 for UTC-10
 * new Date() gets the browsers local time, and .getTimeZoneOffset() returns the -offset in minutes.
 * Therefore we can calculate the browsers current time in relation to the venue
 * for example the browser is in UTC+10 (at 9:52am) and the venue is in UTC+8 (at 7:52am).
 * @param timeZoneOffset The timezone offset of the venue in minutes (e.g., 1000 for UTC+10)
 * @returns related venueTime in ms.
 */
function rdlGetVenueTimeMs(timeZoneOffset) {
  const venueTimeZoneOffsetMin = timeZoneOffset * -1;
  const today = new Date();
  const browserTimeZoneOffset = today.getTimezoneOffset();
  const msDifference = (venueTimeZoneOffsetMin - browserTimeZoneOffset) * 60 * 1000;
  return today.getTime() - msDifference;
}
/**
 * Returns the current date/time of the Venue as a Date instance, using their timezone offset.
 * This can be used to calculate against the venues current time, or compare against the browser time.
 * @param timeZoneOffset The timezone offset of the venue in minutes (e.g., 1000 for UTC+10)
 */
function rdlGetVenueDate(timeZoneOffset) {
  return new Date(rdlGetVenueTimeMs(timeZoneOffset));
}
/**
 * Get the age of a user based on their date of birth and the current date.
 * @param dateString yyyy-mm-dd
 * @param now Date object
 */
function rdlGetAge(dateString, now) {
  // always fixed date set by user so not timezone dependant
  const date = new Date(dateString);
  // use the venues date for age calculations
  let age = now.getFullYear() - date.getFullYear();
  // If the input date's month and day are later in the year than the current date's month and day, subtract 1 from the age
  if (now.getMonth() < date.getMonth() || now.getMonth() === date.getMonth() && now.getDate() < date.getDate()) {
    age--;
  }
  return age;
}

/**
 * Generated bundle index. Do not edit.
 */

export { DateFormatService, DateOnlyStringToDateHelper, DateToStringDateOnlyHyphenHelper, IsSameDayHelper, rdlConvertDateToTimeIndex, rdlConvertTimeIndexToDate, rdlConvertTimeIndexToString, rdlConvertToDate, rdlFormatDate, rdlGetAge, rdlGetVenueDate, rdlGetVenueTimeMs };
